import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate, useParams } from '@tanstack/react-router'
import { AlertTriangle, CheckCircle2, Loader2, Mail, XCircle } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'

import { Button, Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/_components/ui'
import { api } from '@/features/_lib/api'
import { authClient } from '@/features/_lib/auth-client'
import { queries } from '@/features/_lib/react-query'
import { useCompanyStore, useFacilityStore } from '@/features/_stores'

import { AuthLayout } from '../../_components/auth-layout'
import { AuthLegalLinks } from '../../_components/auth-legal-links'

type InvitationStatus = 'checking' | 'needs_auth' | 'email_mismatch' | 'success' | 'error'

const ERROR_MESSAGES: Record<string, string> = {
  ORGANIZATION_NOT_FOUND: 'A empresa associada a este convite não foi encontrada.',
}

function AcceptInvitationContent() {
  const params = useParams({ strict: false }) as { invitationId: string }
  const { invitationId } = params
  const [status, setStatus] = useState<InvitationStatus>('checking')
  const [mismatchEmail, setMismatchEmail] = useState<string>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const hasFired = useRef(false)

  const handleSwitchAccount = async () => {
    await authClient.signOut()
    navigate({
      to: '/login',
      search: { invitationId },
      replace: true,
    })
  }

  const acceptMutation = useMutation({
    mutationFn: async (id: string) => {
      const { data: session } = await authClient.getSession()

      if (!session) {
        return { outcome: 'needs_auth' as const }
      }

      const result = await authClient.organization.acceptInvitation({ invitationId: id })

      if (result.error) {
        const code = result.error.code ?? ''

        // Both "already a member" and "not found" (already accepted) are recoverable
        const recoverableCodes = [
          'USER_IS_ALREADY_A_MEMBER_OF_THIS_ORGANIZATION',
          'INVITATION_NOT_FOUND',
        ]

        if (recoverableCodes.includes(code)) {
          return { outcome: 'already_member' as const }
        }

        if (code === 'YOU_ARE_NOT_THE_RECIPIENT_OF_THE_INVITATION') {
          return { outcome: 'email_mismatch' as const, userEmail: session.user.email }
        }

        const message = ERROR_MESSAGES[code] ?? result.error.message ?? 'Falha ao aceitar convite'
        throw new Error(message)
      }

      return { outcome: 'success' as const }
    },
    onSuccess: async (data) => {
      if (data.outcome === 'needs_auth') {
        setStatus('needs_auth')
        return
      }

      if (data.outcome === 'email_mismatch') {
        setMismatchEmail(data.userEmail)
        setStatus('email_mismatch')
        return
      }

      setStatus('success')
      await redirectToDashboard()
    },
    onError: () => {
      setStatus('error')
    },
  })

  const redirectToDashboard = async () => {
    const companies = await queryClient.fetchQuery(queries.companies.list)

    if (!companies || companies.length === 0) {
      setTimeout(() => {
        navigate({ to: '/login', replace: true })
      }, 2000)
      return
    }

    const [company] = companies

    if (!company) return

    await api.companies.switch(company.id)

    useCompanyStore.getState().setCompany({
      id: company.id,
      slug: company.slug,
      name: company.name,
      cnpj: company.cnpj,
      legalName: company.legalName,
    })

    try {
      const facilities = await queryClient.fetchQuery(queries.companies.facilities(company.id))

      const firstFacility = facilities?.[0]

      if (firstFacility) {
        useFacilityStore.getState().setCurrentFacility(firstFacility)

        setTimeout(() => {
          navigate({
            to: `/companies/${company.slug}/applications`,
            replace: true,
          })
        }, 2000)
        return
      }
    } catch {}

    setTimeout(() => {
      navigate({ to: `/companies/${company.slug}/facilities`, replace: true })
    }, 2000)
  }

  useEffect(() => {
    if (hasFired.current) return
    hasFired.current = true
    acceptMutation.mutate(invitationId)
  }, [invitationId])

  return (
    <AuthLayout>
      <Card className="w-full max-w-[420px] border-0 p-8 shadow-[0_12px_48px_rgba(26,33,52,0.11)]">
        <CardHeader className="p-0 pb-6">
          <CardTitle>Aceitar convite</CardTitle>
          <CardDescription>
            {status === 'checking' && 'Verificando convite...'}
            {status === 'needs_auth' && 'Autenticação necessária'}
            {status === 'email_mismatch' && 'Conta incorreta'}
            {status === 'success' && 'Convite aceito com sucesso!'}
            {status === 'error' && 'Erro ao aceitar convite'}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4 p-0">
          {status === 'checking' && (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-brand-green-primary-5" />
            </div>
          )}

          {status === 'needs_auth' && (
            <div className="space-y-4">
              <div className="rounded-lg border border-blue-200 bg-blue-50 p-4">
                <div className="flex items-start gap-3">
                  <Mail className="h-5 w-5 text-blue-600" />
                  <div className="space-y-1">
                    <p className="text-sm text-blue-700">
                      Crie uma conta ou faça login para aceitar o convite.
                    </p>
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <Button
                  onClick={() =>
                    navigate({
                      to: '/login/sign-up',
                      search: { invitationId },
                    })
                  }
                  className="w-full"
                >
                  Criar conta
                </Button>
                <Button
                  variant="outline"
                  onClick={() =>
                    navigate({
                      to: '/login',
                      search: { invitationId },
                    })
                  }
                  className="w-full"
                >
                  Já tenho uma conta
                </Button>
              </div>
            </div>
          )}

          {status === 'email_mismatch' && (
            <div className="space-y-4">
              <div className="rounded-lg border border-amber-200 bg-amber-50 p-4">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="h-5 w-5 shrink-0 text-amber-600" />
                  <div className="space-y-1">
                    <p className="text-sm font-medium text-amber-800">
                      Este convite foi enviado para outro e-mail
                    </p>
                    <p className="text-sm text-amber-700">
                      Você está logado como <span className="font-medium">{mismatchEmail}</span>.
                      Para aceitar o convite, saia da conta atual e entre com o e-mail correto.
                    </p>
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <Button onClick={handleSwitchAccount} className="w-full">
                  Trocar de conta
                </Button>
                <Button
                  variant="outline"
                  onClick={() => navigate({ to: '/applications', replace: true })}
                  className="w-full"
                >
                  Continuar com esta conta
                </Button>
              </div>
            </div>
          )}

          {status === 'success' && (
            <div className="rounded-lg border border-green-200 bg-green-50 p-4">
              <div className="flex items-start gap-3">
                <CheckCircle2 className="h-5 w-5 text-green-600" />
                <p className="text-sm text-green-800">
                  Você foi adicionado à empresa com sucesso! Redirecionando...
                </p>
              </div>
            </div>
          )}

          {status === 'error' && (
            <div className="space-y-4">
              <div className="rounded-lg border border-red-200 bg-red-50 p-4">
                <div className="flex items-start gap-3">
                  <XCircle className="h-5 w-5 text-red-600" />
                  <p className="text-sm text-red-800">
                    {acceptMutation.error?.message || 'Ocorreu um erro inesperado'}
                  </p>
                </div>
              </div>

              <div className="space-y-2">
                <Button onClick={handleSwitchAccount} className="w-full">
                  Trocar de conta
                </Button>
                <Button
                  variant="outline"
                  onClick={() =>
                    navigate({
                      to: '/login/sign-up',
                      search: { invitationId },
                    })
                  }
                  className="w-full"
                >
                  Criar nova conta
                </Button>
              </div>
            </div>
          )}
        </CardContent>

        <AuthLegalLinks />
      </Card>
    </AuthLayout>
  )
}

export default AcceptInvitationContent
